import { forwardRef, useState, useId } from 'react';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';

/**
 * `invalid` is consumed here and translated to `aria-invalid`, never forwarded.
 *
 * React passes unknown props straight to the DOM, so an `invalid={false}` would
 * otherwise land on the element and warn — and `invalid` is not a real HTML
 * attribute, so it would style nothing either. The component layer styles
 * `[aria-invalid='true']`, which is also what a screen reader reads.
 */
const ariaInvalid = (invalid) => (invalid ? 'true' : undefined);

export const Input = forwardRef(function Input({ className = '', invalid, ...props }, ref) {
  return <input ref={ref} aria-invalid={ariaInvalid(invalid)} className={`input ${className}`} {...props} />;
});

export const Textarea = forwardRef(function Textarea({ className = '', invalid, rows = 3, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={ariaInvalid(invalid)}
      className={`input leading-relaxed ${className}`}
      {...props}
    />
  );
});

export const Select = forwardRef(function Select({ className = '', invalid, children, ...props }, ref) {
  return (
    <div className="relative">
      <select
        ref={ref}
        aria-invalid={ariaInvalid(invalid)}
        className={`input appearance-none pr-9 ${className}`}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-faint"
        aria-hidden="true"
      />
    </div>
  );
});

/**
 * A password field that can be read back.
 *
 * It lives here rather than in each screen because the screens kept forgetting:
 * sign-in had a reveal, registration and the change-password form did not, and
 * nobody notices until they are typing a 20-character generated password into a
 * row of dots for the third time.
 *
 * The toggle is `tabIndex={-1}` on purpose — tabbing from the password field
 * should reach the submit button, not a decoration. A password manager's own
 * button sits at the same corner, so this one is inset to leave it room.
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { className = '', invalid, id, ...props },
  ref,
) {
  const [revealed, setRevealed] = useState(false);
  const generated = useId();
  const inputId = id ?? generated;

  return (
    <div className="relative">
      <input
        ref={ref}
        id={inputId}
        type={revealed ? 'text' : 'password'}
        aria-invalid={ariaInvalid(invalid)}
        className={`input pr-10 ${className}`}
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setRevealed((v) => !v)}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        aria-controls={inputId}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-faint transition-colors hover:bg-surface-2 hover:text-ink"
      >
        {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});

export default Input;
